import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Tooltip,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Skeleton,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { UserRole } from '@/types/user';
import excelIcon from '../../assets/excel-icon.svg';
import {
  useGetRetryMetricsQuery,
  useExportRetryMetricsMutation,
} from '@/services/dashboardApi';
import { useGetAssetsDropdownQuery } from '@/services/assetApi';

interface RetryMetricsSummaryProps {
  amcId?: string;
}

export const RetryMetricsSummary: React.FC<RetryMetricsSummaryProps> = ({ amcId }) => {
  const theme = useTheme();
  const [period, setPeriod] = React.useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [selectedAssetId, setSelectedAssetId] = React.useState<string>('all');
  const [exportRetryMetrics, { isLoading: isExporting }] = useExportRetryMetricsMutation();

  const user = useSelector((state: RootState) => state.user);
  const isSuperUser = user.role
    ? [UserRole.ADMIN, UserRole.SUPPORT].includes(user.role as UserRole)
    : false;

  // For super users: only show asset dropdown when a specific AMC is selected
  // For AMC users: always show it (they always have an AMC context)
  const showAssetDropdown = isSuperUser ? !!amcId : true;

  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  const effectiveAssetId = selectedAssetId === 'all' ? undefined : selectedAssetId;

  const { data, isLoading, isError } = useGetRetryMetricsQuery({
    date: today,
    type: period,
    ...(amcId && { amcId }),
    ...(effectiveAssetId && { assetId: effectiveAssetId }),
  });

  // Only fetch assets when dropdown is visible
  const { data: assetsData } = useGetAssetsDropdownQuery(amcId || undefined, {
    skip: !showAssetDropdown,
  });
  // Response is grouped: { data: [{ amcId, assets: [{id, name}] }] }
  const assets = useMemo(() => {
    if (!assetsData?.data) return [];
    return assetsData.data.flatMap((group: any) => group.assets || []);
  }, [assetsData]);

  const handleExport = async () => {
    try {
      const blob = await exportRetryMetrics({
        date: today,
        type: period,
        ...(amcId && { amcId }),
        ...(effectiveAssetId && { assetId: effectiveAssetId }),
      }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = `retry_metrics_${today}.xlsx`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const stats = [
    {
      label: 'Recovered Transactions',
      value: data?.recoveredTransactions ?? 0,
      color: theme.palette.success.main,
    },
    {
      label: 'Recovery Rate',
      value: `${data?.recoveryRate ?? 0}%`,
      color: theme.palette.primary.main,
    },
    {
      label: 'Recovered Value',
      value: `₹${(data?.recoveredValue ?? 0).toLocaleString('en-IN')}`,
      color: theme.palette.success.dark,
    },
    {
      label: 'Retry Success Contribution',
      value: `${data?.retrySuccessContribution ?? 0}%`,
      color: theme.palette.info.main,
    },
  ];

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: '10px',
        backgroundColor: theme.palette.background.paper,
        width: '100%',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h3" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Retry Payments Summary
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {showAssetDropdown && assets.length > 0 && (
            <FormControl size="small">
              <Select
                value={selectedAssetId}
                onChange={(e) => setSelectedAssetId(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderRadius: '50px' },
                  '& .MuiSelect-select': { padding: '4px 12px', fontSize: '0.75rem' },
                  maxWidth: { xs: 120, sm: 180 },
                }}
              >
                <MenuItem value="all">All Assets</MenuItem>
                {assets.map((asset) => (
                  <MenuItem key={asset.id} value={asset.id}>
                    {asset.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small">
            <Select
              value={period}
              onChange={(e) => setPeriod(e.target.value as 'daily' | 'weekly' | 'monthly')}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': { borderRadius: '50px' },
                '& .MuiSelect-select': { padding: '4px 16px', fontSize: '0.75rem' },
              }}
            >
              <MenuItem value="daily">Today</MenuItem>
              <MenuItem value="weekly">This Week</MenuItem>
              <MenuItem value="monthly">This Month</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Export retry payments to Excel">
            <IconButton
              onClick={handleExport}
              disabled={isExporting || isLoading}
              size="small"
              sx={{
                color: theme.palette.primary.main,
                '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' },
              }}
            >
              {isExporting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <img src={excelIcon} alt="Export to Excel" style={{ width: 20, height: 20 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Stats Grid */}
      {isLoading ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: { xs: 1, sm: 2 },
          }}
        >
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={80} />
          ))}
        </Box>
      ) : isError ? (
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'center', py: 2 }}>
          Failed to load retry metrics
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: { xs: 1, sm: 2 },
          }}
        >
          {stats.map((stat) => (
            <Box
              key={stat.label}
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: '8px',
                border: theme.borders?.borderLight || '1px solid #e0e0e0',
                textAlign: 'center',
                minWidth: 0,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: theme.palette.text.secondary,
                  mb: 0.5,
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {stat.label}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: stat.color,
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {stat.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
